import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, inject } from '@angular/core';

import { BetBuilderOptionGroupInfo, GroupOption, SuspensionState } from '@cds/betting-offer/domain-specific/bet-builder';
import { AutomationDirective } from '@frontend/sports/automation/core-feature';
import { EventModel, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { CustomActiveStateDirective } from '@frontend/sports/common/ui/custom-active-state';
import { HapticsDirective } from '@frontend/sports/haptic-feedback/feature';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { IPickTracking, PickSourceProvider, SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { NOT_APPLICABLE, PRE_CREATED_SGP, TRACKING_NO_GAMEDETAILS, trackingConstants } from '@frontend/sports/tracking/feature';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';

import { ExternalBetslipActions } from '../../betslip/core/external-betslip-actions';
import { V1PickId, V2OptionMarketPickId } from '../../betslip/core/picks/pick-id';
import { getPickIds } from '../../betslip/core/precreated-group-option-helper';
import { getBetslipGroupInformation } from '../bet-builder-core/utils';
import { BetslipUpsellConstants } from '../betslip-base/models/betslip-upsell.model';
import { BetTrackingService } from './bet-tracking.service';
import { OptionIndicatorDirective } from './option-indicator.directive';
import { PickComponent } from './pick.component';

@Component({
    selector: 'ms-betbuilder-group-pick',
    templateUrl: './pick.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CustomActiveStateDirective, OptionIndicatorDirective, AutomationDirective, AsyncPipe, HapticsDirective],
})
export class BetBuilderGroupPickComponent extends PickComponent implements OnChanges, OnDestroy {
    private pickSourceProvider = inject(PickSourceProvider);
    private store = inject(Store);
    private readonly betTrackingService = inject(BetTrackingService);

    @Input() event: EventModel;
    @Input() eventOptionGroups: EventOptionGroup[];
    @Input() betbuilderOptionGroup: BetBuilderOptionGroupInfo;

    groupPickIds: (V1PickId | V2OptionMarketPickId)[] = [];
    precreatedGroup: BetBuilderOptionGroupInfo | undefined;

    get price(): NativePrice {
        if (this.precreatedGroup) {
            return NativePrice.fromNativePrice(this.precreatedGroup.builderOptionPricing.odds);
        }

        return NativePrice.empty();
    }

    get priceId(): number | undefined {
        return undefined;
    }

    ngOnChanges(changes: ISimpleChanges<BetBuilderGroupPickComponent>): void {
        if (changes.betbuilderOptionGroup) {
            this.precreatedGroup = this.event.precreatedOptionGroups?.find((group) => group.groupId === this.betbuilderOptionGroup.groupId);
            if (this.precreatedGroup) {
                this.setGroupPickIds(getPickIds(this.precreatedGroup, this.eventOptionGroups));
                this.state.locked = this.precreatedGroup.builderOptionPricing.suspensionState === SuspensionState.MarketSuspended;
            }
        }
    }

    protected addPick(): void {
        if (this.precreatedGroup) {
            const precreatedGroup = this.precreatedGroup;
            const picks = this.groupPickIds.map((id, index) => {
                return {
                    pickId: id,
                    tracking: this.pickTracking(precreatedGroup.options[index]),
                    groupInfo: getBetslipGroupInformation(this.event),
                    isBetBuilder: true,
                };
            });

            this.store.dispatch(ExternalBetslipActions.addGroupPick({ picks }));
        }
    }

    protected removePick(): void {
        if (this.precreatedGroup) {
            this.store.dispatch(ExternalBetslipActions.removePicksFromGroup({ pickIds: this.groupPickIds }));
        }
    }

    private setGroupPickIds(groupPicksIds: (V1PickId | V2OptionMarketPickId)[]): void {
        if (this.groupPickIds.length === 0) {
            this.groupPickIds = groupPicksIds;
            if (this.pickSelectedSubscription) {
                this.pickSelectedSubscription.unsubscribe();
            }

            const pickSelected$ = combineLatest(this.groupPickIds.map((pickId) => this.betslipIntegration.pickSelected$(pickId)));

            this.pickSelectedSubscription = pickSelected$.subscribe((numerousSelected) => {
                if (numerousSelected.every((isPickSelected) => isPickSelected)) {
                    this.state.selected = true;
                    this.changeDetection.detectChanges();
                }

                if (numerousSelected.some((isPickSelected) => !isPickSelected)) {
                    this.state.selected = false;
                    this.changeDetection.detectChanges();
                }
            });
        }
    }

    private pickTracking(option: GroupOption): IPickTracking {
        const pickSource = this.pickSourceProvider.get(
            this.trackingSource,
            this.trackingPosition,
            this.isFallbackMarketEnabled,
            this.marqueeName,
            this.sitecoreTemplateId,
            this.marqueeType,
            undefined,
            this.isAutomatedMarquee,
        );

        pickSource.additional = pickSource.additional || {};

        const eventOptionGroup = this.eventOptionGroups.flatMap((group) => group.options).find((opt) => option.id === opt.id) || null;
        const hasBetBuilderMarkets =
            this.eventOptionGroups.find((group) => group.options.some((opt) => opt.id === option.id))?.hasBetBuilderMarkets ?? false;

        if (this.canTrackPickDetails()) {
            pickSource.additional[trackingConstants.SPORT_ID] = this.event.sport.id.toString();
            pickSource.additional[trackingConstants.SPORT_LEAGUE_ID] = this.event.league.id.toString();
            pickSource.additional[trackingConstants.RESULT_ID] = option.id.toString();
            pickSource.additional[trackingConstants.MARKET_ID] = option.marketId.toString();
            pickSource.additional[trackingConstants.MARQUEE_CONTENT_LOGIC] = this.isFallbackMarketEnabled ? 'fallback' : 'default';
            pickSource.additional[trackingConstants.MARQUEE_NAME] = this.marqueeName || NOT_APPLICABLE;
            pickSource.additional[trackingConstants.MARQUEE_TYPE] = this.marqueeType || NOT_APPLICABLE;
            pickSource.additional[trackingConstants.SITECORE_TEMPLATE_ID] = this.sitecoreTemplateId || NOT_APPLICABLE;
        }

        pickSource.gameDetails = this.trackingGameDetails ?? TRACKING_NO_GAMEDETAILS;
        pickSource.sourceOfPick = this.sourceOfPick || NOT_APPLICABLE;
        pickSource.betDetails = this.betTrackingService.getBetDetails(
            hasBetBuilderMarkets,
            this.event.betBuilderInfo,
            this.sourceOfPick,
            this.betDetails,
        );
        pickSource.betType = eventOptionGroup!.name;
        pickSource.contentPosition = this.trackingPosition?.toString();
        pickSource.moduleType = this.trackingModuleType;
        pickSource.additional[trackingConstants.BET_CALENDER] = NOT_APPLICABLE;
        pickSource.betPickType = this.getBetType();

        return pickSource;
    }

    private getBetType(): string {
        const provider = this.event.betBuilderInfo?.betBuilderProvider;
        return provider ? `${provider} ${PRE_CREATED_SGP}` : NOT_APPLICABLE;
    }

    private canTrackPickDetails(): boolean {
        return (
            this.trackingSource === BetslipUpsellConstants.TrackingSource ||
            this.sourceOfPick === SourceOfPick.OutrightsGrid ||
            this.sourceOfPick === SourceOfPick.BetStationMarquee
        );
    }
}
